import React, { useState } from "react";
import PropTypes from "prop-types";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import styles from "./EMPConfirmationBanner.module.scss";

const SuccessIcon = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="10.5"
      fill="white"
      stroke="currentColor"
      strokeWidth="2"
    />

    <path
      d="M7.5 12.25L10.5 15.25L16.75 8.75"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

SuccessIcon.propTypes = {
  className: PropTypes.string,
};

SuccessIcon.defaultProps = {
  className: undefined,
};

const CloseIcon = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 6L18 18M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

CloseIcon.propTypes = {
  className: PropTypes.string,
};

CloseIcon.defaultProps = {
  className: undefined,
};

const getBannerPresentation = (variant) => {
  switch (variant) {
    case "error":
      return {
        Icon: ErrorOutlineIcon,
        className: styles.error,
        role: "alert",
        ariaLive: "assertive",
      };

    case "warning":
      return {
        Icon: WarningAmberIcon,
        className: styles.warning,
        role: "alert",
        ariaLive: "polite",
      };

    case "info":
      return {
        Icon: InfoOutlinedIcon,
        className: styles.info,
        role: "status",
        ariaLive: "polite",
      };

    case "success":
    default:
      return {
        Icon: SuccessIcon,
        className: styles.success,
        role: "status",
        ariaLive: "polite",
      };
  }
};

const EMPConfirmationBanner = ({
  primaryText,
  secondaryText,
  variant,
  dismissible,
  closeAriaLabel,
  onClose,
  testId,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible || (!primaryText && !secondaryText)) {
    return null;
  }

  const presentation = getBannerPresentation(variant);
  const BannerIcon = presentation.Icon;

  const handleClose = () => {
    setIsVisible(false);

    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      className={[styles.banner, presentation.className, className]
        .filter(Boolean)
        .join(" ")}
      role={presentation.role}
      aria-live={presentation.ariaLive}
      data-testid={testId}
    >
      <div className={styles.bannerContent}>
        <div className={styles.iconContainer} aria-hidden="true">
          <BannerIcon className={styles.statusIcon} />
        </div>

        <div className={styles.textContainer}>
          {primaryText && (
            <div className={styles.primaryText}>{primaryText}</div>
          )}

          {secondaryText && (
            <div className={styles.secondaryText}>{secondaryText}</div>
          )}
        </div>

        {dismissible && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={closeAriaLabel}
            data-testid={testId ? `${testId}-close` : undefined}
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        )}
      </div>
    </div>
  );
};

EMPConfirmationBanner.propTypes = {
  primaryText: PropTypes.node,
  secondaryText: PropTypes.node,
  variant: PropTypes.oneOf(["success", "error", "warning", "info"]),
  dismissible: PropTypes.bool,
  closeAriaLabel: PropTypes.string,
  onClose: PropTypes.func,
  testId: PropTypes.string,
  className: PropTypes.string,
};

EMPConfirmationBanner.defaultProps = {
  primaryText: null,
  secondaryText: null,
  variant: "success",
  dismissible: true,
  closeAriaLabel: "Close banner",
  onClose: undefined,
  testId: undefined,
  className: undefined,
};

export default EMPConfirmationBanner;
